// Bundles 4 admin pages: Offboarding, Discipline, Safety, Background Checks
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, fmtDay, todayISO } from "@/lib/utils";
import { EmployeePicker, distinguishingLabel, personName } from "@/components/EmployeePicker";
import { plainError } from "@/lib/plain-error";

interface Person { id: string; first_name: string | null; last_name: string | null; email: string; status: string; }

// ── OFFBOARDING ───────────────────────────────────────────────────────────
interface Offboarding {
  id: string; employee_id: string; last_day: string | null; reason: string | null;
  voluntary: boolean | null;
  assets_returned: boolean; accounts_disabled: boolean;
  final_paycheck_sent: boolean; exit_interview_conducted: boolean;
  cobra_offered: boolean; rehire_eligible: boolean;
  exit_interview_notes: string | null;
  completed_at: string | null; notes: string | null;
  created_at: string;
}

export function OffboardingAdmin() {
  const [rows, setRows] = useState<Offboarding[]>([]);
  const [emps, setEmps] = useState<Person[]>([]);
  const [open, setOpen] = useState<Offboarding | null>(null);
  const [newFor, setNewFor] = useState("");
  const [confirmingStart, setConfirmingStart] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  useEffect(() => { void load(); }, []);
  async function load() {
    const [{ data: o }, { data: e }] = await Promise.all([
      supabase.from("hq_offboarding").select("*").order("created_at", { ascending: false }),
      supabase.from("hq_employees_people").select("id,first_name,last_name,email,status").order("first_name"),
    ]);
    setRows((o as Offboarding[]) ?? []);
    setEmps((e as Person[]) ?? []);
  }
  const empById = useMemo(() => Object.fromEntries(emps.map(e => [e.id, e])), [emps]);

  async function start() {
    if (!newFor) return;
    // HQ-OFFBOARD-SELECTION-001: Begin used to insert immediately, from a list
    // where nine people showed as "QA". Completing that record deactivates
    // whichever hidden id was selected. One confirmation naming the person.
    setStartError(null);
    const person = empById[newFor];
    if (!confirmingStart) { setConfirmingStart(true); return; }
    const { data, error } = await supabase.from("hq_offboarding")
      .insert({ employee_id: newFor, last_day: todayISO() })
      .select().single();
    if (error) { setStartError(plainError(error, "start offboarding")); return; }
    if (data) { setNewFor(""); setConfirmingStart(false); await load(); setOpen(data as Offboarding); }
    void person;
  }

  /** HQ-OFFBOARD-SELECTION-001: a record begun in error had no way back. */
  async function cancelOffboarding(r: Offboarding) {
    if (r.completed_at) return;   // a closed record is history, not a draft
    setStartError(null);
    const { error } = await supabase.from("hq_offboarding").delete().eq("id", r.id);
    if (error) { setStartError(plainError(error, "cancel that offboarding")); return; }
    setOpen(null);
    await load();
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold tracking-tight">Offboarding</h1>

      {startError && <div className="rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">{startError}</div>}
      <div className="card space-y-3">
        <div className="flex items-end gap-3">
          <EmployeePicker
            className="flex-1"
            label="Start offboarding for"
            people={emps.filter(e => e.status === "active" && !rows.find(r => r.employee_id === e.id && !r.completed_at))}
            value={newFor}
            onChange={id => { setNewFor(id); setConfirmingStart(false); }}
          />
          {!confirmingStart && (
            <button onClick={() => void start()} disabled={!newFor} className="btn">Begin</button>
          )}
        </div>
        {confirmingStart && newFor && (
          <div className="rounded-lg bg-warn-bg px-3.5 py-3 text-sm">
            <p className="font-semibold text-warn">
              Begin offboarding {empById[newFor] ? personName(empById[newFor]) : "this employee"}?
            </p>
            <p className="mt-1 text-xs text-gray2">
              Completing the record deactivates their account. You can cancel the record before then.
            </p>
            <div className="mt-2 flex gap-2">
              <button onClick={() => void start()} className="btn btn-sm">Yes, begin</button>
              <button onClick={() => setConfirmingStart(false)} className="btn btn-sm btn-ghost">Cancel</button>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-2">
        {rows.map(r => {
          const e = empById[r.employee_id];
          const pct = checklistPct(r);
          return (
            <button key={r.id} onClick={() => setOpen(r)} className="block w-full card text-left transition hover:ring-teal">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-semibold">{e ? personName(e) : "—"}</h2>
                  <div className="text-xs text-gray2">Last day: {fmtDay(r.last_day)} · Started {fmtDate(r.created_at)}</div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="text-xl font-bold text-teal-dark">{pct}%</div>
                    <div className="text-xs text-gray2">{r.completed_at ? "Closed" : "In progress"}</div>
                  </div>
                </div>
              </div>
            </button>
          );
        })}
        {rows.length === 0 && <div className="card py-10 text-center text-sm text-gray2">No offboarding records.</div>}
      </div>

      {open && <OffboardingModal off={open} employee={empById[open.employee_id]} onClose={() => setOpen(null)} onChanged={load} onCancelRecord={cancelOffboarding} />}
    </div>
  );
}

function checklistPct(r: Offboarding): number {
  const total = 6;
  const done = (r.assets_returned ? 1 : 0) + (r.accounts_disabled ? 1 : 0) +
    (r.final_paycheck_sent ? 1 : 0) + (r.exit_interview_conducted ? 1 : 0) +
    (r.cobra_offered ? 1 : 0) + (r.completed_at ? 1 : 0);
  return Math.round((done / total) * 100);
}

function OffboardingModal({ off, employee, onClose, onChanged, onCancelRecord }: { off: Offboarding; employee: Person | undefined; onClose: () => void; onChanged: () => Promise<void>; onCancelRecord: (r: Offboarding) => Promise<void> }) {
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [r, setR] = useState<Offboarding>(off);
  async function patch(p: Partial<Offboarding>) {
    setR({ ...r, ...p });
    await supabase.from("hq_offboarding").update(p).eq("id", r.id);
  }
  async function complete() {
    // Deactivate employee + close
    await supabase.from("hq_employees_people").update({ status: "inactive", termination_date: r.last_day }).eq("id", r.employee_id);
    await patch({ completed_at: new Date().toISOString() });
    await onChanged(); onClose();
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/60 p-4" onClick={onClose}>
      <div className="max-h-[92vh] w-full max-w-2xl overflow-auto rounded-xl bg-white p-6 shadow-soft" onClick={e => e.stopPropagation()}>
        <h2 className="mb-4 text-xl font-bold">Offboarding: {employee ? (employee.first_name ?? employee.email) : "—"}</h2>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="label">Last day</label><input type="date" className="input" value={r.last_day ?? ""} onChange={e => patch({ last_day: e.target.value || null })} /></div>
            <div><label className="label">Reason</label><select className="input" value={r.reason ?? ""} onChange={e => patch({ reason: e.target.value || null })}><option value="">—</option>{["resignation","termination","layoff","retirement","end_of_contract","other"].map(s => <option key={s}>{s.replace(/_/g, " ")}</option>)}</select></div>
            <div><label className="label">Voluntary?</label><select className="input" value={r.voluntary === null ? "" : r.voluntary ? "yes" : "no"} onChange={e => patch({ voluntary: e.target.value === "" ? null : e.target.value === "yes" })}><option value="">—</option><option value="yes">Yes</option><option value="no">No</option></select></div>
            <div className="flex items-end"><label className="flex items-center gap-2 text-sm rounded-lg bg-offwhite px-3 py-2 cursor-pointer w-full"><input type="checkbox" checked={r.rehire_eligible} onChange={e => patch({ rehire_eligible: e.target.checked })} /> Eligible for rehire</label></div>
          </div>

          <div>
            <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-gray2">Checklist</h3>
            <div className="space-y-1">
              {([
                ["assets_returned", "Assets returned (laptop, tools, keys, badge)"],
                ["accounts_disabled", "System accounts disabled (email, HQ, Drive, etc.)"],
                ["final_paycheck_sent", "Final paycheck sent"],
                ["exit_interview_conducted", "Exit interview conducted"],
                ["cobra_offered", "COBRA / health insurance continuation offered"],
              ] as const).map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 rounded-lg p-2 cursor-pointer hover:bg-offwhite">
                  <input type="checkbox" checked={r[k]} onChange={e => patch({ [k]: e.target.checked })} className="h-5 w-5" />
                  <span className={r[k] ? "line-through text-gray2" : ""}>{label}</span>
                </label>
              ))}
            </div>
          </div>

          <div><label className="label">Exit interview notes</label><textarea className="input min-h-[120px]" value={r.exit_interview_notes ?? ""} onChange={e => patch({ exit_interview_notes: e.target.value || null })} /></div>
          <div><label className="label">Notes</label><textarea className="input" rows={2} value={r.notes ?? ""} onChange={e => patch({ notes: e.target.value || null })} /></div>

          {!r.completed_at && (
            <div className="rounded-lg bg-warn-bg p-4">
              <div className="mb-2 text-sm">
                Completing this will deactivate {employee ? personName(employee) : "this employee"}'s
                account and mark their last day.
              </div>
              <button onClick={complete} className="btn">Complete offboarding &amp; deactivate</button>
            </div>
          )}

          {/* HQ-OFFBOARD-SELECTION-001: a record begun for the wrong person had
              no way back — no cancel, no remove. Only before completion: once
              the account is deactivated this is history, not a draft. */}
          {!r.completed_at && (
            <div className="border-t border-line pt-3">
              {confirmingCancel ? (
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-bad">
                    Remove this offboarding record for {employee ? personName(employee) : "this employee"}?
                  </span>
                  <button onClick={() => void onCancelRecord(r)} className="btn btn-sm" style={{ background: "var(--bad)" }}>
                    Yes, remove it
                  </button>
                  <button onClick={() => setConfirmingCancel(false)} className="btn btn-sm btn-ghost">Keep it</button>
                </div>
              ) : (
                <button onClick={() => setConfirmingCancel(true)} className="text-sm font-medium text-bad hover:underline">
                  Started this for the wrong person? Remove the record
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── DISCIPLINARY ACTIONS ──────────────────────────────────────────────────
interface Discipline {
  id: string; employee_id: string; issued_by: string; issued_on: string;
  category: string | null; severity: string | null;
  incident_description: string; policy_violated: string | null;
  action_taken: string; improvement_plan: string | null;
  next_review_date: string | null;
  employee_signed_at: string | null; employee_comments: string | null;
  created_at: string;
}

export function DisciplineAdmin() {
  const { employee } = useAuth();
  const [rows, setRows] = useState<Discipline[]>([]);
  const [emps, setEmps] = useState<Person[]>([]);
  const [editing, setEditing] = useState<Partial<Discipline> | null>(null);

  useEffect(() => { void load(); }, []);
  async function load() {
    const [{ data: r }, { data: e }] = await Promise.all([
      supabase.from("hq_disciplinary_actions").select("*").order("issued_on", { ascending: false }),
      supabase.from("hq_employees_people").select("id,first_name,last_name,email,status").eq("status", "active"),
    ]);
    setRows((r as Discipline[]) ?? []); setEmps((e as Person[]) ?? []);
  }
  const empById = useMemo(() => Object.fromEntries(emps.map(e => [e.id, e])), [emps]);

  async function save() {
    if (!editing?.employee_id || !editing.incident_description || !editing.action_taken || !employee) return;
    const payload = {
      employee_id: editing.employee_id, issued_by: employee.id,
      issued_on: editing.issued_on ?? new Date().toISOString().slice(0,10),
      category: editing.category ?? null, severity: editing.severity ?? null,
      incident_description: editing.incident_description,
      policy_violated: editing.policy_violated ?? null,
      action_taken: editing.action_taken,
      improvement_plan: editing.improvement_plan ?? null,
      next_review_date: editing.next_review_date ?? null,
    };
    if (editing.id) await supabase.from("hq_disciplinary_actions").update(payload).eq("id", editing.id);
    else await supabase.from("hq_disciplinary_actions").insert(payload);
    setEditing(null); await load();
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Disciplinary Actions</h1>
        <button onClick={() => setEditing({ issued_on: new Date().toISOString().slice(0,10), severity: "verbal_warning" })} className="btn">+ New write-up</button>
      </div>

      <div className="space-y-2">
        {rows.map(r => {
          const e = empById[r.employee_id];
          return (
            <div key={r.id} className="card">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="font-semibold">{e ? personName(e) : "—"} · {fmtDay(r.issued_on)}</h2>
                  <div className="flex gap-2 mt-1">
                    {r.severity && <span className={`pill ${severityPill(r.severity)}`}>{r.severity.replace(/_/g, " ")}</span>}
                    {r.category && <span className="pill pill-mint">{r.category}</span>}
                    {r.employee_signed_at ? <span className="pill pill-ok">✓ Signed</span> : <span className="pill pill-warn">Pending sig</span>}
                  </div>
                </div>
                <button onClick={() => setEditing(r)} className="btn btn-sm btn-ghost">Edit</button>
              </div>
              <div className="mt-3 text-sm">{r.incident_description}</div>
              {r.action_taken && <div className="mt-2 text-sm"><strong>Action:</strong> {r.action_taken}</div>}
            </div>
          );
        })}
        {rows.length === 0 && <div className="card py-10 text-center text-sm text-gray2">No actions recorded.</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/60 p-4" onClick={() => setEditing(null)}>
          <div className="max-h-[92vh] w-full max-w-2xl overflow-auto rounded-xl bg-white p-6 shadow-soft" onClick={e => e.stopPropagation()}>
            <h2 className="mb-4 text-xl font-bold">{editing.id ? "Edit" : "New"} disciplinary action</h2>
            <div className="space-y-3">
              <div><label className="label">Employee</label><select className="input" value={editing.employee_id ?? ""} onChange={e => setEditing({ ...editing, employee_id: e.target.value })}><option value="">—</option>{emps.map(e => <option key={e.id} value={e.id}>{distinguishingLabel(e, emps)}</option>)}</select></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">Issued on</label><input type="date" className="input" value={editing.issued_on ?? ""} onChange={e => setEditing({ ...editing, issued_on: e.target.value })} /></div>
                <div><label className="label">Severity</label><select className="input" value={editing.severity ?? ""} onChange={e => setEditing({ ...editing, severity: e.target.value || null })}><option value="">—</option>{["verbal_warning","written_warning","final_warning","suspension","termination"].map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}</select></div>
              </div>
              <div><label className="label">Category</label><input className="input" value={editing.category ?? ""} onChange={e => setEditing({ ...editing, category: e.target.value || null })} placeholder="attendance, conduct, safety…" /></div>
              <div><label className="label">Incident description *</label><textarea className="input" rows={3} value={editing.incident_description ?? ""} onChange={e => setEditing({ ...editing, incident_description: e.target.value })} /></div>
              <div><label className="label">Policy violated</label><input className="input" value={editing.policy_violated ?? ""} onChange={e => setEditing({ ...editing, policy_violated: e.target.value || null })} /></div>
              <div><label className="label">Action taken *</label><textarea className="input" rows={2} value={editing.action_taken ?? ""} onChange={e => setEditing({ ...editing, action_taken: e.target.value })} /></div>
              <div><label className="label">Improvement plan</label><textarea className="input" rows={2} value={editing.improvement_plan ?? ""} onChange={e => setEditing({ ...editing, improvement_plan: e.target.value || null })} /></div>
              <div><label className="label">Next review date</label><input type="date" className="input" value={editing.next_review_date ?? ""} onChange={e => setEditing({ ...editing, next_review_date: e.target.value || null })} /></div>
              <div className="flex justify-end gap-2"><button onClick={() => setEditing(null)} className="btn btn-ghost">Cancel</button><button onClick={save} className="btn" disabled={!editing.employee_id || !editing.incident_description || !editing.action_taken}>Save</button></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function severityPill(s: string): string {
  if (s === "termination") return "pill-bad";
  if (s === "suspension" || s === "final_warning") return "pill-bad";
  if (s === "written_warning") return "pill-warn";
  return "pill-mint";
}

// ── SAFETY INCIDENTS ──────────────────────────────────────────────────────
interface Incident {
  id: string; incident_date: string; incident_time: string | null;
  location: string | null; incident_type: string | null;
  severity: string | null;
  injured_employee_ids: string[]; reported_by: string | null;
  description: string;
  immediate_actions: string | null; root_cause: string | null;
  corrective_actions: string | null; osha_recordable: boolean;
  workers_comp_claim: string | null;
  closed_at: string | null; created_at: string;
}

export function SafetyAdmin() {
  const [rows, setRows] = useState<Incident[]>([]);
  const [emps, setEmps] = useState<Person[]>([]);
  const [editing, setEditing] = useState<Partial<Incident> | null>(null);
  const { employee } = useAuth();

  useEffect(() => { void load(); }, []);
  async function load() {
    const [{ data: r }, { data: e }] = await Promise.all([
      supabase.from("hq_safety_incidents").select("*").order("incident_date", { ascending: false }),
      supabase.from("hq_employees_people").select("id,first_name,last_name,email,status").eq("status", "active"),
    ]);
    setRows((r as Incident[]) ?? []); setEmps((e as Person[]) ?? []);
  }

  async function save() {
    if (!editing?.incident_date || !editing.description || !employee) return;
    const payload = {
      incident_date: editing.incident_date, incident_time: editing.incident_time ?? null,
      location: editing.location ?? null, incident_type: editing.incident_type ?? null,
      severity: editing.severity ?? null,
      injured_employee_ids: editing.injured_employee_ids ?? [],
      reported_by: employee.id, description: editing.description,
      immediate_actions: editing.immediate_actions ?? null,
      root_cause: editing.root_cause ?? null,
      corrective_actions: editing.corrective_actions ?? null,
      osha_recordable: editing.osha_recordable ?? false,
      workers_comp_claim: editing.workers_comp_claim ?? null,
    };
    if (editing.id) await supabase.from("hq_safety_incidents").update(payload).eq("id", editing.id);
    else await supabase.from("hq_safety_incidents").insert(payload);
    setEditing(null); await load();
  }

  const recordable = rows.filter(r => r.osha_recordable).length;
  const last30d = rows.filter(r => Date.now() - new Date(r.incident_date).getTime() < 30 * 86400000).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Safety Incidents</h1>
        <button onClick={() => setEditing({ incident_date: new Date().toISOString().slice(0,10), severity: "near_miss", injured_employee_ids: [] })} className="btn">+ Log incident</button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Stat label="Total" value={String(rows.length)} />
        <Stat label="OSHA recordable" value={String(recordable)} />
        <Stat label="Last 30 days" value={String(last30d)} />
      </div>

      <div className="space-y-2">
        {rows.map(r => (
          /* HQ-HR-A11Y-001. This was a clickable <div>: the accessibility tree
             showed a heading and a paragraph and no control at all, so the only
             way to open a safety incident was to click it with a mouse. It is a
             button now, with a name that says which incident it opens — a list
             of cards all announced as "Open incident" would be no better than
             the div was. */
          <button
            key={r.id}
            type="button"
            onClick={() => setEditing(r)}
            aria-label={`Open incident: ${fmtDay(r.incident_date)}${r.location ? ` at ${r.location}` : ""}`
              + `${r.severity ? `, ${r.severity.replace(/_/g, " ")}` : ""}${r.closed_at ? ", closed" : ""}`}
            className="card w-full text-left transition hover:ring-teal focus-visible:ring-2 focus-visible:ring-teal"
          >
            <div className="flex items-start justify-between">
              <div>
                <h2 className="font-semibold">{fmtDay(r.incident_date)}{r.location ? ` · ${r.location}` : ""}</h2>
                <div className="flex gap-2 mt-1">
                  {r.severity && <span className={`pill ${incidentSevPill(r.severity)}`}>{r.severity.replace(/_/g, " ")}</span>}
                  {r.osha_recordable && <span className="pill pill-bad">OSHA</span>}
                  {r.closed_at && <span className="pill pill-ok">closed</span>}
                </div>
              </div>
            </div>
            <p className="mt-2 text-sm">{r.description}</p>
          </button>
        ))}
        {rows.length === 0 && <div className="card py-10 text-center text-sm text-gray2">No incidents on record.</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/60 p-4" onClick={() => setEditing(null)}>
          <div className="max-h-[92vh] w-full max-w-2xl overflow-auto rounded-xl bg-white p-6 shadow-soft" onClick={e => e.stopPropagation()}>
            <h2 className="mb-4 text-xl font-bold">{editing.id ? "Edit" : "Log"} safety incident</h2>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">Date</label><input type="date" className="input" value={editing.incident_date ?? ""} onChange={e => setEditing({ ...editing, incident_date: e.target.value })} /></div>
                <div><label className="label">Time</label><input type="time" className="input" value={editing.incident_time ?? ""} onChange={e => setEditing({ ...editing, incident_time: e.target.value || null })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">Location</label><input className="input" value={editing.location ?? ""} onChange={e => setEditing({ ...editing, location: e.target.value || null })} placeholder="Warehouse, office, off-site…" /></div>
                <div><label className="label">Type</label><input className="input" value={editing.incident_type ?? ""} onChange={e => setEditing({ ...editing, incident_type: e.target.value || null })} placeholder="Slip, cut, burn, equipment…" /></div>
              </div>
              <div><label className="label">Severity</label><select className="input" value={editing.severity ?? ""} onChange={e => setEditing({ ...editing, severity: e.target.value || null })}><option value="">—</option>{["near_miss","first_aid","medical","lost_time","fatality"].map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}</select></div>
              <div><label className="label">Injured employees</label>
                <select multiple className="input min-h-[80px]" value={editing.injured_employee_ids ?? []} onChange={e => {
                  const sel = Array.from(e.target.selectedOptions).map(o => o.value);
                  setEditing({ ...editing, injured_employee_ids: sel });
                }}>{emps.map(e => <option key={e.id} value={e.id}>{distinguishingLabel(e, emps)}</option>)}</select>
                <p className="mt-1 text-xs text-gray2">Hold Cmd/Ctrl to select multiple.</p>
              </div>
              <div><label className="label">Description *</label><textarea className="input" rows={4} value={editing.description ?? ""} onChange={e => setEditing({ ...editing, description: e.target.value })} /></div>
              <div><label className="label">Immediate actions taken</label><textarea className="input" rows={2} value={editing.immediate_actions ?? ""} onChange={e => setEditing({ ...editing, immediate_actions: e.target.value || null })} /></div>
              <div><label className="label">Root cause</label><textarea className="input" rows={2} value={editing.root_cause ?? ""} onChange={e => setEditing({ ...editing, root_cause: e.target.value || null })} /></div>
              <div><label className="label">Corrective actions</label><textarea className="input" rows={2} value={editing.corrective_actions ?? ""} onChange={e => setEditing({ ...editing, corrective_actions: e.target.value || null })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex items-center gap-2 text-sm rounded-lg bg-offwhite px-3 py-2"><input type="checkbox" checked={editing.osha_recordable ?? false} onChange={e => setEditing({ ...editing, osha_recordable: e.target.checked })} /> OSHA recordable</label>
                <div><label className="label">Workers comp claim #</label><input className="input" value={editing.workers_comp_claim ?? ""} onChange={e => setEditing({ ...editing, workers_comp_claim: e.target.value || null })} /></div>
              </div>
              <div className="flex justify-end gap-2"><button onClick={() => setEditing(null)} className="btn btn-ghost">Cancel</button><button onClick={save} className="btn" disabled={!editing.incident_date || !editing.description}>Save</button></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function incidentSevPill(s: string): string {
  if (s === "fatality" || s === "lost_time") return "pill-bad";
  if (s === "medical") return "pill-warn";
  return "pill-mint";
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="card">
      <div className="text-xs uppercase tracking-wider text-gray2">{label}</div>
      <div className="mt-1 text-2xl font-bold text-teal-dark">{value}</div>
    </div>
  );
}

// ── BACKGROUND CHECKS ─────────────────────────────────────────────────────
interface BG {
  id: string; employee_id: string; check_type: string; vendor: string | null;
  status: string; requested_at: string | null; completed_at: string | null;
  expires_at: string | null; consent_signed_at: string | null;
  notes: string | null;
}

export function BackgroundChecksAdmin() {
  const [rows, setRows] = useState<BG[]>([]);
  const [emps, setEmps] = useState<Person[]>([]);
  const [editing, setEditing] = useState<Partial<BG> | null>(null);

  useEffect(() => { void load(); }, []);
  async function load() {
    const [{ data: r }, { data: e }] = await Promise.all([
      supabase.from("hq_background_checks").select("*").order("completed_at", { ascending: false, nullsFirst: false }),
      supabase.from("hq_employees_people").select("id,first_name,last_name,email,status").eq("status", "active"),
    ]);
    setRows((r as BG[]) ?? []); setEmps((e as Person[]) ?? []);
  }
  const empById = useMemo(() => Object.fromEntries(emps.map(e => [e.id, e])), [emps]);

  async function save() {
    if (!editing?.employee_id || !editing.check_type) return;
    const payload = {
      employee_id: editing.employee_id, check_type: editing.check_type,
      vendor: editing.vendor ?? null, status: editing.status ?? "requested",
      requested_at: editing.requested_at ?? null, completed_at: editing.completed_at ?? null,
      expires_at: editing.expires_at ?? null,
      consent_signed_at: editing.consent_signed_at ?? null,
      notes: editing.notes ?? null,
    };
    if (editing.id) await supabase.from("hq_background_checks").update(payload).eq("id", editing.id);
    else await supabase.from("hq_background_checks").insert(payload);
    setEditing(null); await load();
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Background Checks</h1>
        <button onClick={() => setEditing({ check_type: "criminal", status: "requested" })} className="btn">+ New check</button>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-line text-left text-xs uppercase tracking-wider text-gray2"><th className="py-2">Employee</th><th>Type</th><th>Vendor</th><th>Status</th><th>Completed</th><th>Expires</th><th></th></tr></thead>
          <tbody>
            {rows.map(r => {
              const e = empById[r.employee_id];
              const expired = r.expires_at && new Date(r.expires_at) < new Date();
              return (
                <tr key={r.id} className="border-b border-line/70">
                  <td className="py-2">{e ? personName(e) : "—"}</td>
                  <td className="py-2 text-gray2">{r.check_type}</td>
                  <td className="py-2 text-gray2">{r.vendor ?? "—"}</td>
                  <td className="py-2"><span className={`pill ${bgStatusPill(r.status, expired)}`}>{expired ? "expired" : r.status.replace(/_/g, " ")}</span></td>
                  <td className="py-2 text-gray2">{r.completed_at ? fmtDate(r.completed_at) : "—"}</td>
                  <td className="py-2 text-gray2">{r.expires_at ? fmtDay(r.expires_at) : "—"}</td>
                  <td className="py-2 text-right"><button onClick={() => setEditing(r)} className="btn btn-sm btn-ghost">Edit</button></td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-sm text-gray2">No background checks.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/60 p-4" onClick={() => setEditing(null)}>
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-soft" onClick={e => e.stopPropagation()}>
            <h2 className="mb-4 text-xl font-bold">{editing.id ? "Edit" : "New"} background check</h2>
            <div className="space-y-3">
              <div><label className="label">Employee</label><select className="input" value={editing.employee_id ?? ""} onChange={e => setEditing({ ...editing, employee_id: e.target.value })}><option value="">—</option>{emps.map(e => <option key={e.id} value={e.id}>{distinguishingLabel(e, emps)}</option>)}</select></div>
              <div><label className="label">Type</label><select className="input" value={editing.check_type ?? ""} onChange={e => setEditing({ ...editing, check_type: e.target.value })}>{["criminal","employment","education","credit","drug_screen","motor_vehicle","reference","social_media","other"].map(t => <option key={t}>{t.replace(/_/g, " ")}</option>)}</select></div>
              <div><label className="label">Vendor</label><input className="input" value={editing.vendor ?? ""} onChange={e => setEditing({ ...editing, vendor: e.target.value || null })} placeholder="Checkr, Sterling, etc." /></div>
              <div><label className="label">Status</label><select className="input" value={editing.status ?? "requested"} onChange={e => setEditing({ ...editing, status: e.target.value })}>{["requested","in_progress","passed","flagged","failed","expired"].map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}</select></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">Completed</label><input type="date" className="input" value={editing.completed_at ? editing.completed_at.slice(0,10) : ""} onChange={e => setEditing({ ...editing, completed_at: e.target.value ? new Date(e.target.value).toISOString() : null })} /></div>
                <div><label className="label">Expires</label><input type="date" className="input" value={editing.expires_at ?? ""} onChange={e => setEditing({ ...editing, expires_at: e.target.value || null })} /></div>
              </div>
              <div><label className="label">Notes</label><textarea className="input" rows={2} value={editing.notes ?? ""} onChange={e => setEditing({ ...editing, notes: e.target.value || null })} /></div>
              <div className="flex justify-end gap-2"><button onClick={() => setEditing(null)} className="btn btn-ghost">Cancel</button><button onClick={save} className="btn" disabled={!editing.employee_id || !editing.check_type}>Save</button></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function bgStatusPill(s: string, expired: boolean | null | undefined | string): string {
  if (expired) return "pill-bad";
  if (s === "passed") return "pill-ok";
  if (s === "flagged" || s === "failed") return "pill-bad";
  if (s === "in_progress") return "pill-warn";
  return "pill-mint";
}
